import React, { useState, useMemo } from 'react';
import { useTranslation } from '../../i18n';
import { getPackHeaderTranslated, getGameLinkName } from '../../i18n/helpers';
import '../../App.css';
import { AppHeader } from './AppHeader';
import { PackHeaderTitle } from './PackHeaderTitle';
import BgAnimate from './BgAnimate/BgAnimate';
import LoadingSpinner from './LoadingSpinner';
import { BurgerMenu } from '../menu/Menu';
import MenuPageWrapper from '../menu/MenuPageWrapper';
import HowToPlayPage from '../menu/HowToPlayPage';
import PremiumPage from '../menu/PremiumPage';
import HelpPage from '../menu/HelpPage';
import { appVariant } from '../../config';
import { getMenuGameLinks } from '../../config/gameLinks';

type MenuPage = 'how-to-play' | 'premium' | 'support' | null;

interface RoomLoadingShellProps {
  /** Optional label shown above the spinner (e.g. "Creating your room") */
  label?: string;
}

/**
 * Shown when we have saved room data but roomState is not yet restored (e.g. after refresh),
 * or while processing a host deep link (?host= / /host).
 * Uses the same header as game setup (logo, pack name/slogan, menu) and the same spinner
 * as LoadingQuestion so the spinner stays in the same position when transitioning.
 */
const RoomLoadingShell: React.FC<RoomLoadingShellProps> = ({ label }) => {
  const { t } = useTranslation();
  const [currentMenuPage, setCurrentMenuPage] = useState<MenuPage>(null);

  const getGameType = (): 'blitz' | 'team' | 'family' | 'party' => {
    const variantMap: Record<string, 'blitz' | 'team' | 'family' | 'party'> = {
      blitz: 'blitz',
      team: 'team',
      family: 'family',
      party: 'party',
      coop: 'blitz',
    };
    return variantMap[appVariant] || 'blitz';
  };

  const { name: packName, phrase: packPhrase } = getPackHeaderTranslated(appVariant);

  const menuItems = useMemo(() => {
    const gameLinks = getMenuGameLinks(getGameType());
    return [
      { id: 'play-another-game-header', label: t('lobby.menu.playAnotherGamePack'), isSectionHeader: true },
      ...gameLinks.map((link) => ({ id: link.id, label: getGameLinkName(link.id), href: link.url })),
      { id: 'menu-divider', isDivider: true },
      { id: 'menu-section-header', label: t('lobby.menu.sectionHeader'), isSectionHeader: true },
      { id: 'how-to-play', label: t('common.howToPlay'), onClick: () => setCurrentMenuPage('how-to-play') },
      { id: 'premium', label: t('common.premium'), onClick: () => setCurrentMenuPage('premium') },
      { id: 'support', label: t('common.support'), onClick: () => setCurrentMenuPage('support') },
    ];
  }, [t]);

  if (currentMenuPage === 'how-to-play') {
    return (
      <MenuPageWrapper
        title={t('common.howToPlay')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <HowToPlayPage />
      </MenuPageWrapper>
    );
  }
  if (currentMenuPage === 'premium') {
    return (
      <MenuPageWrapper
        title={t('common.premium')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <PremiumPage gameType={getGameType()} />
      </MenuPageWrapper>
    );
  }
  if (currentMenuPage === 'support') {
    return (
      <MenuPageWrapper
        title={t('common.support')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <HelpPage />
      </MenuPageWrapper>
    );
  }

  return (
    <div className="app room-setup-app" style={{ position: 'relative' }} data-testid="room-loading-shell">
      <BgAnimate speed={2.0} delayBetweenCycles={2000} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <AppHeader
          rightButton={undefined}
          centerContent={<PackHeaderTitle name={packName} phrase={packPhrase} />}
          rightContent={<BurgerMenu items={menuItems} />}
        />
        <div
          className="container"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 100px)',
            paddingTop: '100px',
          }}
        >
          {label ? (
            <p
              className="title"
              style={{
                margin: '0 0 1.25rem',
                color: 'var(--color-primary)',
                fontSize: '1.25rem',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              {label}
            </p>
          ) : null}
          <LoadingSpinner />
        </div>
      </div>
    </div>
  );
};

export default RoomLoadingShell;
